import React, { useEffect, useState } from 'react';
import { Loading } from '../../components/Loading';
import {
  Leaderboard,
  LeaderboardRecordsLog,
  LeaderboardTab,
  TimePeriod,
} from '../../types/types';
import { stringToUpperCaseFirstLetter } from '../../utils/helperFunctions';
import { LeaderboardCard } from '../../components/LeaderboardCard';
import {
  fetchCurrentTopLeaderboards,
  fetchLeaderboardRecordsLogs,
  fetchRecordsLeaderboards,
} from '../../services/api';
import { TimePeriodSelection } from '../../components/TimePeriodSelection';
import { Button, Tab, Tabs } from '@mui/material';
import { DateSelection } from '../../components/DateSelection';
import { Dayjs } from 'dayjs';

const Leaderboards: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [leaderboards, setLeaderboards] = useState<Leaderboard | undefined>(
    undefined,
  );
  const [leaderboardRecordsLogs, setLeaderboardRecordsLogs] = useState<
    LeaderboardRecordsLog[] | undefined
  >(undefined);
  const [showDateSelection, setShowDateSelection] = useState<boolean>(true);
  const [tabValue, setTabValue] = React.useState<number>(0);
  const [timePeriod, setTimePeriod] = useState<TimePeriod>('day');
  const [amountOfRows, setAmountOfRows] = useState<number>(10);

  useEffect(() => {
    handlefetchCurrentTopLeaderboardsByTimePeriod('day');
  }, []);

  const handleSetTimePeriod = (newTimePeriod: TimePeriod) => {
    setTimePeriod(newTimePeriod);
    if (tabValue === 0) {
      handlefetchCurrentTopLeaderboardsByTimePeriod(newTimePeriod);
    } else if (tabValue === 1) {
      handlefetchRecordsLeaderboardsByTimePeriod(newTimePeriod);
    }
  };

  const handlefetchCurrentTopLeaderboardsByTimePeriod = async (
    timePeriod: TimePeriod,
  ) => {
    try {
      if (timePeriod === 'day') {
        setShowDateSelection(true);
      } else {
        setShowDateSelection(false);
      }
      setLoading(true);
      const result = await fetchCurrentTopLeaderboards(timePeriod);
      setLeaderboards(result);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const handlefetchCurrentTopLeaderboardsByDate = async (date: Dayjs) => {
    try {
      setLoading(true);
      const result = await fetchCurrentTopLeaderboards('day', date);
      setLeaderboards(result);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const handleFetchLeaderboardRecordsLogsByDate = async (date?: Dayjs) => {
    try {
      setLoading(true);
      const result = await fetchLeaderboardRecordsLogs(date);
      setLeaderboardRecordsLogs(result);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const handlefetchRecordsLeaderboardsByTimePeriod = async (
    timePeriod: TimePeriod,
  ) => {
    try {
      setLoading(true);
      const result = await fetchRecordsLeaderboards(timePeriod);
      setLeaderboards(result);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const a11yProps = (index: number) => {
    return {
      id: `simple-tab-${index}`,
      'aria-controls': `simple-tabpanel-${index}`,
    };
  };

  const handleTabChange = (
    event: React.SyntheticEvent,
    newTabValue: number,
  ) => {
    event.preventDefault();
    setAmountOfRows(10);
    setTimePeriod('day');
    if (newTabValue === 0) {
      handlefetchCurrentTopLeaderboardsByTimePeriod('day');
    } else if (newTabValue === 1) {
      setShowDateSelection(false);
      handlefetchRecordsLeaderboardsByTimePeriod('day');
    } else if (newTabValue === 2) {
      setShowDateSelection(true);
      handleFetchLeaderboardRecordsLogsByDate();
    }
    setTabValue(newTabValue);
  };

  const deriveTabValueStringFromNumber = (): LeaderboardTab => {
    if (tabValue === 1) {
      return 'records';
    }
    if (tabValue === 2) {
      return 'logs';
    }
    return 'currentTop';
  };

  const leaderboardRecordsLogsGroupedByTimePeriod =
    leaderboardRecordsLogs?.reduce<Record<TimePeriod, LeaderboardRecordsLog[]>>(
      (acc, log) => {
        (acc[log.time_period] ??= []).push(log);
        return acc;
      },
      { day: [], week: [], month: [], year: [] },
    );

  const timePeriodLabels: Record<TimePeriod, string> = {
    day: 'Day',
    week: 'Week',
    month: 'Month',
    year: 'Year',
  };

  return (
    <div className="leaderboardsPage">
      <h1 className="title">Leaderboards</h1>
      <div className="leaderboardTabContainer">
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label="Leaderboard tabs"
        >
          <Tab label="Current Top" aria-label="Current Top" {...a11yProps(0)} />
          <Tab label="Records" aria-label="Records" {...a11yProps(1)} />
          <Tab label="Logs" aria-label="Logs" {...a11yProps(2)} />
        </Tabs>
      </div>
      {tabValue !== 2 && (
        <div className="leaderboardsPageTimePeriodSelection">
          <TimePeriodSelection
            timePeriod={timePeriod}
            handleFetch={handleSetTimePeriod}
          />
        </div>
      )}
      {showDateSelection && (
        <div className="leaderboardsPageDateSelection">
          {tabValue === 0 && (
            <DateSelection
              handleFetch={handlefetchCurrentTopLeaderboardsByDate}
            />
          )}
          {tabValue === 2 && (
            <div>
              <div className="marginTopContainer">
                New records from each daily update starting 09/30/2025
              </div>
              <DateSelection
                handleFetch={handleFetchLeaderboardRecordsLogsByDate}
              />
            </div>
          )}
        </div>
      )}
      {loading ? (
        <Loading />
      ) : (
        <div>
          {tabValue !== 2 ? (
            amountOfRows === 5 ? (
              <Button onClick={() => setAmountOfRows(10)}>TOP 10</Button>
            ) : (
              <Button onClick={() => setAmountOfRows(5)}>TOP 5</Button>
            )
          ) : null}
          {leaderboards && tabValue !== 2 && (
            <div className="leaderboardCardsContainer">
              {Object.keys(leaderboards).map((skill) => {
                return (
                  <LeaderboardCard
                    key={skill}
                    skill={stringToUpperCaseFirstLetter(skill)}
                    leaderboardData={leaderboards[skill as keyof Leaderboard]}
                    selectedTab={deriveTabValueStringFromNumber()}
                    amountOfRows={amountOfRows}
                  />
                );
              })}
            </div>
          )}
          {leaderboardRecordsLogsGroupedByTimePeriod && tabValue === 2 && (
            <div className="leaderboardRecordsLogsContainer">
              {(Object.keys(timePeriodLabels) as TimePeriod[]).map(
                (timePeriod) =>
                  leaderboardRecordsLogsGroupedByTimePeriod[timePeriod].length >
                  0 ? (
                    <div key={timePeriod} className="timePeriodBlock">
                      <h3 className="timePeriodTitle">
                        {timePeriodLabels[timePeriod]}
                      </h3>
                      <div className="logsList">
                        {leaderboardRecordsLogsGroupedByTimePeriod[timePeriod]
                          .sort(
                            (
                              a: LeaderboardRecordsLog,
                              b: LeaderboardRecordsLog,
                            ) => a.rank - b.rank,
                          )
                          .map((log: LeaderboardRecordsLog) => (
                            <div key={log.id} className="logRow">
                              {`Rank ${log.rank} - ${log.skill.charAt(0).toUpperCase() + log.skill.slice(1)} - ${log.exp_gain.toLocaleString('en-US')} exp - ${log.player_name}`}
                            </div>
                          ))}
                      </div>
                    </div>
                  ) : null,
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Leaderboards;
